import { ShopOutlined, ShoppingCartOutlined, TeamOutlined, TruckOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Popconfirm, notification } from 'antd';
import { FC, useCallback, useState } from 'react';
import { errorObjectToString } from 'src/utils/common';
import { callSyncFirestoreToBigQuery } from 'src/utils/firebase/firebase-callable';

import classes from './AppConfig/AppConfig.module.scss';

type TableName = 'store' | 'product' | 'partners_user' | 'product_supplier' | 'staff_user';
type Action = 'initialize' | 'insert';

const TABLE_LABELS: Record<TableName, string> = {
  store: '매장',
  product: '상품',
  partners_user: '배송멤버',
  product_supplier: '공급사',
  staff_user: '직원',
};

const ACTION_LABELS: Record<Action, string> = {
  initialize: '일괄 삭제',
  insert: '업데이트',
};

const ExtraConfig: FC = () => {
  const [loadingStore, setLoadingStore] = useState<boolean>(false);
  const [loadingProduct, setLoadingProduct] = useState<boolean>(false);
  const [loadingPartnersUser, setLoadingPartnersUser] = useState<boolean>(false);
  const [loadingProductSupplier, setLoadingProductSupplier] = useState<boolean>(false);
  const [loadingStaffUser, setLoadingStaffUser] = useState<boolean>(false);

  const syncTable = useCallback(
    async (tableName: TableName, action: Action, setLoading: (loading: boolean) => void) => {
      const tableLabel = TABLE_LABELS[tableName];
      const actionLabel = ACTION_LABELS[action];

      setLoading(true);
      try {
        const response = await callSyncFirestoreToBigQuery({
          tableName,
          action,
          extraInfo: null,
        });

        if (response.data.result !== 'success') {
          throw new Error(response.data.message);
        }
        notification.success({
          message: `${tableLabel} 정보 ${actionLabel} 성공`,
          description: response.data.message,
        });
      } catch (error) {
        console.error(error);
        const errorMessage = errorObjectToString(error);
        notification.error({
          message: `${tableLabel} 정보 ${actionLabel} 실패`,
          description: errorMessage,
        });
      }
      setLoading(false);
    },
    [],
  );

  const resetStoreTable = () => syncTable('store', 'initialize', setLoadingStore);
  const updateStoreTable = () => syncTable('store', 'insert', setLoadingStore);
  const resetProductTable = () => syncTable('product', 'initialize', setLoadingProduct);
  const updateProductTable = () => syncTable('product', 'insert', setLoadingProduct);
  const resetPartnersUserTable = () => syncTable('partners_user', 'initialize', setLoadingPartnersUser);
  const updatePartnersUserTable = () => syncTable('partners_user', 'insert', setLoadingPartnersUser);
  const resetProductSupplierTable = () => syncTable('product_supplier', 'initialize', setLoadingProductSupplier);
  const updateProductSupplierTable = () => syncTable('product_supplier', 'insert', setLoadingProductSupplier);
  const resetStaffUserTable = () => syncTable('staff_user', 'initialize', setLoadingStaffUser);
  const updateStaffUserTable = () => syncTable('staff_user', 'insert', setLoadingStaffUser);

  return (
    <div className={classes.appConfigContainer}>
      <p>
        ※ 중복 데이터 업로드를 방지하려면 기존 데이터를 삭제한 후 업데이트를 진행해 주세요.
        <br />
        삭제된 테이블이 준비되는 데 시간이 소요될 수 있으므로, 삭제 후 <b>5분 뒤에</b> 업데이트를 진행하실 것을
        권장합니다.
      </p>
      <div className={classes.tableGrid}>
        <div>
          <h2>Tablue store table</h2>
          <div className={classes.formContainer}>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>매장 정보 일괄 삭제 : </p>
              <Popconfirm
                title='매장 정보 초기화'
                description='BigQuery의 매장 정보 테이블을 초기화합니다. 계속하시겠습니까?'
                okText='초기화'
                cancelText='취소'
                okButtonProps={{ loading: loadingStore, danger: true }}
                onConfirm={resetStoreTable}
              >
                <Button icon={<ShopOutlined />} danger loading={loadingStore}>
                  Reset store table
                </Button>
              </Popconfirm>
            </div>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>매장 정보 업데이트 : </p>
              <Popconfirm
                title='매장 정보 업데이트'
                description='BigQuery의 매장 정보 테이블을 업데이트합니다. 계속하시겠습니까?'
                okText='업데이트'
                cancelText='취소'
                okButtonProps={{ loading: loadingStore }}
                onConfirm={updateStoreTable}
              >
                <Button icon={<ShopOutlined />} type='primary' loading={loadingStore}>
                  Update store table
                </Button>
              </Popconfirm>
            </div>
          </div>
        </div>
        <div>
          <h2>Tablue product table</h2>
          <div className={classes.formContainer}>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>상품 정보 일괄 삭제 : </p>
              <Popconfirm
                title='상품 정보 초기화'
                description='BigQuery의 상품 정보 테이블을 초기화합니다. 계속하시겠습니까?'
                okText='초기화'
                cancelText='취소'
                okButtonProps={{ loading: loadingProduct, danger: true }}
                onConfirm={resetProductTable}
              >
                <Button icon={<ShoppingCartOutlined />} danger loading={loadingProduct}>
                  Reset product table
                </Button>
              </Popconfirm>
            </div>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>상품 정보 업데이트 : </p>
              <Popconfirm
                title='상품 정보 업데이트'
                description='BigQuery의 상품 정보 테이블을 업데이트합니다. 계속하시겠습니까?'
                okText='업데이트'
                cancelText='취소'
                okButtonProps={{ loading: loadingProduct }}
                onConfirm={updateProductTable}
              >
                <Button icon={<ShoppingCartOutlined />} type='primary' loading={loadingProduct}>
                  Update product table
                </Button>
              </Popconfirm>
            </div>
          </div>
        </div>
        <div>
          <h2>Tablue partners_user table</h2>
          <div className={classes.formContainer}>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>배송멤버 정보 일괄 삭제 : </p>
              <Popconfirm
                title='배송멤버 정보 초기화'
                description='BigQuery의 배송멤버 정보 테이블을 초기화합니다. 계속하시겠습니까?'
                okText='초기화'
                cancelText='취소'
                okButtonProps={{ loading: loadingPartnersUser, danger: true }}
                onConfirm={resetPartnersUserTable}
              >
                <Button icon={<UserOutlined />} danger loading={loadingPartnersUser}>
                  Reset partners_user table
                </Button>
              </Popconfirm>
            </div>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>배송멤버 정보 업데이트 : </p>
              <Popconfirm
                title='배송멤버 정보 업데이트'
                description='BigQuery의 배송멤버 정보 테이블을 업데이트합니다. 계속하시겠습니까?'
                okText='업데이트'
                cancelText='취소'
                okButtonProps={{ loading: loadingPartnersUser }}
                onConfirm={updatePartnersUserTable}
              >
                <Button icon={<UserOutlined />} type='primary' loading={loadingPartnersUser}>
                  Update partners_user table
                </Button>
              </Popconfirm>
            </div>
          </div>
        </div>
        <div>
          <h2>Tablue product_supplier table</h2>
          <div className={classes.formContainer}>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>공급사 정보 일괄 삭제 : </p>
              <Popconfirm
                title='공급사 정보 초기화'
                description='BigQuery의 공급사 정보 테이블을 초기화합니다. 계속하시겠습니까?'
                okText='초기화'
                cancelText='취소'
                okButtonProps={{ loading: loadingProductSupplier, danger: true }}
                onConfirm={resetProductSupplierTable}
              >
                <Button icon={<TruckOutlined />} danger loading={loadingProductSupplier}>
                  Reset product_supplier table
                </Button>
              </Popconfirm>
            </div>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>공급사 정보 업데이트 : </p>
              <Popconfirm
                title='공급사 정보 업데이트'
                description='BigQuery의 공급사 정보 테이블을 업데이트합니다. 계속하시겠습니까?'
                okText='업데이트'
                cancelText='취소'
                okButtonProps={{ loading: loadingProductSupplier }}
                onConfirm={updateProductSupplierTable}
              >
                <Button icon={<TruckOutlined />} type='primary' loading={loadingProductSupplier}>
                  Update product_supplier table
                </Button>
              </Popconfirm>
            </div>
          </div>
        </div>
        <div>
          <h2>Tablue staff_user table</h2>
          <div className={classes.formContainer}>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>직원 정보 일괄 삭제 : </p>
              <Popconfirm
                title='직원 정보 초기화'
                description='BigQuery의 직원 정보 테이블을 초기화합니다. 계속하시겠습니까?'
                okText='초기화'
                cancelText='취소'
                okButtonProps={{ loading: loadingStaffUser, danger: true }}
                onConfirm={resetStaffUserTable}
              >
                <Button icon={<TeamOutlined />} danger loading={loadingStaffUser}>
                  Reset staff_user table
                </Button>
              </Popconfirm>
            </div>
            <div className={classes.buttonWithLabel}>
              <p className={classes.labelText}>직원 정보 업데이트 : </p>
              <Popconfirm
                title='직원 정보 업데이트'
                description='BigQuery의 직원 정보 테이블을 업데이트합니다. 계속하시겠습니까?'
                okText='업데이트'
                cancelText='취소'
                okButtonProps={{ loading: loadingStaffUser }}
                onConfirm={updateStaffUserTable}
              >
                <Button icon={<TeamOutlined />} type='primary' loading={loadingStaffUser}>
                  Update staff_user table
                </Button>
              </Popconfirm>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtraConfig;
